// Settings templates (architecture sprint-11 §8.4). Template changes never touch existing projects, so
// only the template keys are invalidated.
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { apiRequest, apiRequestNoContent } from '@/lib/api';
import type { SettingKey, SettingModeValue, TemplateDetail, TemplateListItem } from '@/types/settings';

export const TEMPLATES_KEY = ['admin', 'templates'] as const;

export function useTemplates(enabled = true): UseQueryResult<TemplateListItem[]> {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: async () => (await apiRequest<{ items: TemplateListItem[] }>('/api/admin/templates')).items,
    enabled,
  });
}

export function useTemplate(id: string): UseQueryResult<TemplateDetail> {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, id],
    queryFn: () => apiRequest<TemplateDetail>(`/api/admin/templates/${id}`),
    retry: false,
    meta: { silent: true },
  });
}

function useInvalidateTemplates(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
}

export function useCopyTemplate(): UseMutationResult<TemplateDetail, Error, { sourceTemplateId: string; name: string }> {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (body) => apiRequest<TemplateDetail>('/api/admin/templates', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

export function useRenameTemplate(): UseMutationResult<TemplateDetail, Error, { id: string; name: string }> {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: ({ id, name }) => apiRequest<TemplateDetail>(`/api/admin/templates/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    onSuccess: invalidate,
  });
}

export function useDeleteTemplate(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (id) => apiRequestNoContent(`/api/admin/templates/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useSetTemplateValue(id: string): UseMutationResult<TemplateDetail, Error, { key: SettingKey; value: unknown; mode?: SettingModeValue }> {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: ({ key, value, mode }) =>
      apiRequest<TemplateDetail>(`/api/admin/templates/${id}/values/${key}`, {
        method: 'PUT',
        body: JSON.stringify(mode === undefined ? { value } : { value, mode }),
      }),
    onSuccess: invalidate,
  });
}

export function useUnsetTemplateValue(id: string): UseMutationResult<void, Error, SettingKey> {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (key) => apiRequestNoContent(`/api/admin/templates/${id}/values/${key}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
